import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { differenceInMilliseconds, format } from "date-fns";
import { toast } from "react-toastify";
import { toZonedTime } from "date-fns-tz";
import { useRecoilState, useRecoilValue } from "recoil";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import {
  Grid,
  Paper,
  Box,
  Button,
  Typography,
  Avatar,
  Divider,
  Chip,
  Skeleton
} from "@mui/material";
import { captionSelectedImage } from "../store/atom";

import { useQuery } from "react-query";
import {
  createContestActivity,
  getAllContestActivities,
  getAllContests,
  getAllContestVotesByUserId
} from "../apis/contestApi";
// @ts-ignore
import { isEmpty, rangeRight, truncate } from "lodash";
import ContestSocials from "./ContestSocials";
import ContestActivities from "./ContestActivities";
import ContestFooter from "./ContestFooter";
import { telegramInfoState } from "../store/atom";
import { getStatusColorByStatusName } from "./utils/getStatusColorByStatusName";

const PAGE_LIMIT = 100;
type AvailableView = "info" | "activities" | "submit";
const AVAILABLE_VIEWS: AvailableView[] = ["info", "activities", "submit"];

const ContestDetail = () => {
  const { contestId } = useParams();
  const telegramIdInfo = useRecoilValue(telegramInfoState);
  const [selectedImageData, setSelectedImageData] =
    useRecoilState(captionSelectedImage);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File>();
  const [page, setPage] = useState(1);
  const [currentView, setCurrentView] = useState<AvailableView>();

  const userTelegramId = telegramIdInfo?.currentConnectedTelegramId;

  const { data: hotContestsData, isFetched: isFetchedContestByContestId } =
    useQuery(
      `getContestByContestId-${contestId}`,
      () =>
        getAllContests({
          contestIds: [contestId || "NA"]
        }),
      {
        enabled: !!contestId,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        refetchOnReconnect: false
      }
    );

  const {
    data: contestActivitiesResult,
    refetch: refetchFetAllContestActivities,
    isLoading: isActivitiesLoading,
    isFetched: isActivitiesFetched
  } = useQuery(
    `getAllContestActivities-${contestId}-${page}`,
    () =>
      getAllContestActivities(
        contestId || "",
        PAGE_LIMIT,
        (page - 1) * PAGE_LIMIT
      ),
    {
      enabled: !!contestId,
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  const { data: userContestVotes = [], refetch: refetchUserContestVotes } =
    useQuery(
      `getAllContestVotesByUserId-${contestId}-${userTelegramId}`,
      () => getAllContestVotesByUserId(contestId!, userTelegramId),
      {
        enabled: !!contestId && !!userTelegramId,
        retry: false,
        refetchOnWindowFocus: false
      }
    );

  const [contest] = hotContestsData?.contests ?? [];
  const isContestExpiredOrCompleted =
    contest?.is_expired || contest?.status !== "pending";
  const contestActivities = contestActivitiesResult?.activities ?? [];
  const contestActivitiesCount = contestActivitiesResult?.totalCount ?? 0;

  const allPrizes = useMemo(() => {
    const prizes = [
      { label: "1st prize", value: contest?.first_prize ?? 0 },
      { label: "2nd prize", value: contest?.second_prize ?? 0 },
      { label: "3rd prize", value: contest?.third_prize ?? 0 },
      { label: "Others", value: contest?.other_prize ?? 0 }
    ];

    const totalPrizesArray = rangeRight(contest?.total_prize || 1, 0);

    // Sum logic: First 3 values use first, second, third prize; the rest use Others
    // @ts-ignore
    const totalPrizeAmount = totalPrizesArray.reduce((sum, num, index) => {
      if (index === 0) return sum + Number(contest?.first_prize || "0");
      if (index === 1) return sum + Number(contest?.second_prize || "0");
      if (index === 2) return sum + Number(contest?.third_prize || "0");
      return sum + Number(contest?.other_prize || "0");
    }, 0);

    return {
      prizes,
      totalPrizeAmount
    };
  }, [contest]);

  // Initialize the target date to 3 days ahead in the local time zone
  const targetDate = useRef(new Date().toISOString());

  useEffect(() => {
    if (contest?.end_time && !isContestExpiredOrCompleted) {
      targetDate.current = String(contest?.end_time);
    }
  }, [contest]);

  useEffect(() => {
    refetchFetAllContestActivities();
  }, [page]);

  const calculateTimeLeft = () => {
    if (isContestExpiredOrCompleted) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    const now = new Date();
    const contestEndTime = targetDate.current;

    const distance = differenceInMilliseconds(contestEndTime, now);

    if (distance <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    };
  };

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());

  useEffect(() => {
    if (!contest?.end_time || isContestExpiredOrCompleted) return;

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [contest]);

  const timeBoxes = [
    { label: "Days", value: timeLeft?.days ?? 0 },
    { label: "Hours", value: timeLeft?.hours ?? 0 },
    { label: "Minutes", value: timeLeft?.minutes ?? 0 },
    { label: "Seconds", value: timeLeft?.seconds ?? 0 }
  ];

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: (files) => {
      const file = files[0];
      const url = URL.createObjectURL(file);
      setFile(file);
      setImageUrl(url);
      setSelectedImageData({
        ...selectedImageData,
        imageUrl: url
      });
    }
  });

  const handleSubmitMemeContent = async () => {
    try {
      if (!(file && contest)) {
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("project_id", contest.project_id);
      formData.append("contest_id", contest.id);
      formData.append("user_id", userTelegramId);

      await createContestActivity(formData);

      setFile(undefined);
      setImageUrl("");
      toast.success("Content submitted successfully!", {
        autoClose: 5000
      });
      refetchFetAllContestActivities();
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.message ?? "Content submission failed";
      toast.error(errorMessage, {
        autoClose: 5000
      });
    }
  };
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  if (isEmpty(contest)) {
    return (
      <Box>
        <Paper
          sx={{
            borderRadius: "32px",
            overflow: "hidden",
            backgroundColor: "inherit",
            margin: 2
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} lg={9}>
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={"50vh"}
                sx={{
                  borderRadius: "16px",
                  backgroundColor: "#245c9c"
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton
                variant="rectangular"
                width={"100%"}
                height={"50vh"}
                sx={{ borderRadius: "16px", backgroundColor: "#245c9c" }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid item xs={12} md={8} lg={9}>
          {/* Information */}
          {(!currentView || currentView === "info") && (
            <Paper
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                borderRadius: "32px",
                overflow: "hidden",
                backgroundColor: "#245c9c",
                padding: "16px",
                m: 2,
                mb: 0
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "auto",
                  paddingTop: "40.33%",
                  overflow: "hidden",
                  zIndex: 1,
                  marginBottom: "-10%",
                  borderRadius: "24px",
                  backgroundImage: `url("${
                    contest.contest_banner ||
                    `https://pbs.twimg.com/profile_banners/1775113200578506752/1718877939/1080x360`
                  }")`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                  backgroundPosition: "top"
                }}
              ></Box>
              <Box
                sx={{
                  padding: "8px",
                  zIndex: 10,
                  backgroundColor: "#245c9c",
                  borderRadius: "32px"
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0}
                  className="token-header"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    className="token-profile"
                    sx={{ flexGrow: 1, overflow: "hidden" }}
                  >
                    <Avatar
                      src={contest.logo_url || contest.contest_banner}
                      sx={{
                        width: 50,
                        height: 50,
                        flexShrink: 0
                      }}
                    />
                    <Box sx={{ minWidth: 0, marginLeft: "4px" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "20px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        noWrap
                      >
                        {contest.name
                          ? `${`${contest.name} ${
                              contest.symbol ? ` | ${contest.symbol}` : ""
                            }`}`
                          : contest.contest_name}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            top: -15,
                            mt: 2,
                            height: "25px",
                            backgroundColor: getStatusColorByStatusName(
                              contest?.status as any
                            ),
                            color: "white",
                            borderRadius: "16px",
                            textDecoration: "none",
                            textTransform: "none"
                          }}
                        >
                          {contest.status === "pending" && !contest.is_expired
                            ? "Active"
                            : contest.status}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#f5f5f5",
                      textOverflow: "ellipsis",
                      minWidth: 0
                    }}
                  >
                    {truncate(contest.contest_name, {
                      length: 50
                    })}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "#eee",
                      fontWeight: "400",
                      fontSize: "16px",
                      whiteSpace: "wrap",
                      textOverflow: "ellipsis",
                      minWidth: 0
                    }}
                  >
                    {truncate(
                      contest.description
                        ? contest.description
                        : contest.contest_name,
                      {
                        length: 250
                      }
                    )}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ gap: 1, marginTop: "8px" }}
                >
                  <Chip
                    color="success"
                    size="small"
                    label={
                      <Typography
                        gutterBottom
                        variant="body1"
                        component="span"
                        color="#f5f5f5"
                        fontSize={"13px"}
                      >
                        {contest?.total_prize ?? "0"} winner
                        {Number(contest?.total_prize || "0") > 1 ? "s" : ""}
                      </Typography>
                    }
                  />
                  <Chip
                    color="primary"
                    size="small"
                    label={
                      <Typography
                        gutterBottom
                        variant="body1"
                        component="span"
                        color="#f5f5f5"
                        fontSize={"13px"}
                      >
                        {allPrizes?.totalPrizeAmount?.toFixed(2)}{" "}
                        {contest.currency}
                      </Typography>
                    }
                  />
                </Box>
                <Box
                  sx={{
                    color: "white",
                    borderRadius: "16px",
                    marginTop: "8px"
                  }}
                >
                  <Box display="flex" justifyContent="flex-start">
                    <ContestSocials
                      telegram={contest.telegram}
                      twitter={contest.twitter}
                      website={contest.website}
                      chart={contest.contract}
                      chain={contest.chain}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Submitted contests or activities */}
          {(!currentView || currentView === "activities") && (
            <Box sx={{ margin: "0px 8px" }}>
              <ContestActivities
                contestId={contest.id}
                data={contestActivities}
                isLoading={isActivitiesLoading && !isActivitiesFetched}
                userId={userTelegramId}
                userContestVotes={userContestVotes}
                refetchUserContestVotes={refetchUserContestVotes}
                refetchAllContestActivities={refetchFetAllContestActivities}
                isExpired={isContestExpiredOrCompleted}
                pagination={{
                  contestActivitiesCount: contestActivitiesCount,
                  count: Math.ceil(contestActivitiesCount / PAGE_LIMIT),
                  page: page,
                  limit: PAGE_LIMIT,
                  handleChangePage: handleChangePage
                }}
              />
            </Box>
          )}
        </Grid>

        {/* Sidebar contents */}
        {(!currentView || currentView === "submit") && (
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              sx={{
                color: "white",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                borderRadius: "32px",
                overflow: "hidden",
                backgroundColor: "#245c9c",
                padding: "16px",
                m: 2,
                ml: {
                  xs: 2,
                  lg: 0
                }
              }}
            >
              {contest.is_expired || contest.status !== "pending" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    alignItems: "center",
                    mb: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Contest Expired
                  </Typography>
                  <Divider />
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    flexDirection="column"
                  >
                    <Box
                      sx={{
                        height: 40,
                        p: 2,
                        borderRadius: 2,
                        width: "auto",
                        border: "2px dashed #ffe263",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2
                      }}
                    >
                      <Typography width={"100%"}>
                        The contest is not accepting submission anymore.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    alignItems: "center",
                    mb: 2
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Submit Content
                  </Typography>
                  <Divider />
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    flexDirection="column"
                  >
                    <Typography variant="body1">
                      upload Your meme here to take part in the contests
                    </Typography>
                    {userTelegramId ? (
                      <Box
                        {...getRootProps()}
                        sx={{
                          height: 40,
                          p: 2,
                          borderRadius: 2,
                          width: "auto",
                          border: "2px dashed #ffe263",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0
                        }}
                      >
                        <input {...getInputProps()} />
                        <Typography width={"100%"} fontSize="13px">
                          Click or drop an image or a gif file here
                        </Typography>
                        <CloudUploadIcon sx={{ color: "#ffe263", margin: 1 }} />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 40,
                          p: 2,
                          borderRadius: 2,
                          width: "auto",
                          border: "2px dashed #ffe263",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0
                        }}
                      >
                        <Typography width={"100%"} fontSize="13px">
                          Login and connect your Telegram id to submit.
                        </Typography>
                        <InfoOutlined sx={{ color: "#ffe263", margin: 1 }} />
                      </Box>
                    )}
                    <Box
                      height="auto"
                      width={"auto"}
                      sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        "& img": {
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          border: "1px solid white",
                          borderRadius: 2,
                          mb: 1
                        }
                      }}
                    >
                      {imageUrl && <img src={imageUrl} alt={imageUrl} />}
                    </Box>
                    <Box textAlign={"center"}>
                      <Button
                        variant="contained"
                        onClick={handleSubmitMemeContent}
                        disabled={!(userTelegramId && file)}
                        sx={{
                          width: "100%",
                          backgroundColor: "#ffe263",
                          "&:hover": {
                            backgroundColor: "#ffb703"
                          },
                          color: "#000"
                        }}
                      >
                        Submit
                      </Button>
                      {!(userTelegramId && file) && (
                        <Typography fontSize={"13px"} color={"#FFE263"}>
                          Attach file to submit
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
            <Paper
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                borderRadius: "32px",
                overflow: "hidden",
                backgroundColor: "#245c9c",
                padding: "16px",
                m: 2,
                ml: {
                  xs: 2,
                  lg: 0
                },
                mt: {
                  xs: 0,
                  lg: 2
                }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  alignItems: "center",
                  mb: 3
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: "white" }}>
                  Contest Expires In
                </Typography>
                <Box display="flex" justifyContent="center" gap={2}>
                  {timeBoxes.map((box, index) => (
                    <Box
                      key={index}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      sx={{
                        width: 50,
                        height: 50,
                        backgroundColor: "#F8D7DA",
                        color: "#000",
                        borderRadius: "8px",
                        display: "flex",
                        justifyContent: "center"
                      }}
                    >
                      <Typography variant="h6">
                        {String(box.value).padStart(2, "0")}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider />
              <Box
                sx={{ display: "flex", height: "40px", alignItems: "center" }}
              >
                <Typography variant="body2" sx={{ color: "white" }}>
                  Total Votes
                </Typography>
                <Typography variant="body1" sx={{ color: "white", ml: "auto" }}>
                  {Number(contest.votes_count)?.toFixed(0)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{ display: "flex", height: "40px", alignItems: "center" }}
              >
                <Typography variant="body2" sx={{ color: "white" }}>
                  Submissions Count
                </Typography>
                <Typography variant="body1" sx={{ color: "white", ml: "auto" }}>
                  {Number(contest.activities_count)?.toFixed(0)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{ display: "flex", height: "40px", alignItems: "center" }}
              >
                {" "}
                <Typography variant="body2" sx={{ color: "white" }}>
                  Created On
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "white", ml: "auto", fontSize: 13 }}
                >
                  {contest.start_time
                    ? `${format(
                        toZonedTime(contest.start_time, "UTC"),
                        "yyyy-MM-dd HH:mm"
                      )} UTC`
                    : "N/A"}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  height: "40px",
                  alignItems: "center"
                }}
              >
                {" "}
                <Typography variant="body2" sx={{ color: "white" }}>
                  Ends On
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "white", ml: "auto", fontSize: 13 }}
                >
                  {contest.end_time
                    ? `${format(
                        toZonedTime(contest.end_time, "UTC"),
                        "yyyy-MM-dd HH:mm"
                      )} UTC`
                    : "N/A"}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Footer */}
        <ContestFooter
          options={AVAILABLE_VIEWS.map((view) => {
            const isDisabled = view === "submit" && isContestExpiredOrCompleted;
            return {
              label: view,
              onClick: (selectedView: AvailableView) =>
                setCurrentView(selectedView),
              disabled: isDisabled,
              isSelected: view === currentView
            };
          })}
        />
      </Grid>
    </Box>
  );
};

export default ContestDetail;
